import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buscarOpcoesDoRelatorio,
  FormatoRelatorio,
  gerarRelatorioDeVeiculos,
  mensagemDeErroDoRelatorio,
  ModoDoRelatorio,
  ModoRelatorio,
  OpcoesDoRelatorio,
} from '../../../api/reportsApi';
import { ReportFilters } from '../../../interfaces/ReportFilters';
import {
  ApprovalStatusMap,
  DamageTypeMap,
  DamageTypeOptions,
} from '../../../components/mappings/textMappings';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { notify } from '../../../shared/feedback/notify';

import './CarsReportModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import '../../../shared/feedback/erro-de-tela.css';

interface CarsReportModalProps {
  isOpen: boolean;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

/**
 * Gerar Relatório de Veículos (at-107).
 *
 * O cardápio de colunas vem do BACKEND (`GET /report/cars/opcoes`), nunca de uma lista escrita
 * aqui: até esta entrega as colunas eram declaradas nos dois repositórios, e coluna que só
 * existia no frontend virava célula vazia no arquivo.
 */
const CarsReportModal: React.FC<CarsReportModalProps> = ({ isOpen, onCancel, sidebarRef }) => {
  const { esteirasAtivas } = useEsteiras();

  const [opcoes, setOpcoes] = useState<OpcoesDoRelatorio | null>(null);
  const [carregandoOpcoes, setCarregandoOpcoes] = useState<boolean>(true);
  /**
   * at-60: sem o catálogo, não dá para saber quais colunas existem — e gerar "com o que sobrou"
   * produziria um arquivo silenciosamente incompleto. A geração fica BLOQUEADA com a explicação,
   * em vez de oferecer um botão que entrega menos do que parece.
   */
  const [falhouOCatalogo, setFalhouOCatalogo] = useState<boolean>(false);

  const [modoId, setModoId] = useState<ModoRelatorio>('ESTEIRAS');
  const [formato, setFormato] = useState<FormatoRelatorio>('CSV');
  const [camposSelecionados, setCamposSelecionados] = useState<string[]>([]);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filtros, setFiltros] = useState<ReportFilters>({});
  const [gerando, setGerando] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const modo: ModoDoRelatorio | null = useMemo(
    () => opcoes?.modos.find((item) => item.id === modoId) ?? null,
    [opcoes, modoId],
  );

  /** As colunas agrupadas por seção — 40 caixas soltas numa grade não se leem. */
  const secoes = useMemo(() => {
    if (!modo) return [] as Array<{ nome: string; colunas: ModoDoRelatorio['colunas'] }>;
    const mapa = new Map<string, ModoDoRelatorio['colunas']>();
    for (const coluna of modo.colunas) {
      mapa.set(coluna.secao, [...(mapa.get(coluna.secao) ?? []), coluna]);
    }
    return Array.from(mapa.entries()).map(([nome, colunas]) => ({ nome, colunas }));
  }, [modo]);

  const carregarOpcoes = useCallback(async () => {
    setCarregandoOpcoes(true);
    try {
      const catalogo = await buscarOpcoesDoRelatorio();
      setOpcoes(catalogo);
      setFalhouOCatalogo(false);

      const inicial =
        catalogo.modos.find((item) => item.id === catalogo.modoPadrao) ?? catalogo.modos[0];
      if (inicial) {
        setModoId(inicial.id);
        setCamposSelecionados(inicial.padrao);
      }
    } catch (erro) {
      setFalhouOCatalogo(true);
      notify.errorFrom(
        erro,
        'Não foi possível carregar as opções do relatório. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregandoOpcoes(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) carregarOpcoes();
  }, [isOpen, carregarOpcoes]);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = sidebarRef?.current && !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onCancel();
    };

    if (isOpen) document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [isOpen, onCancel, sidebarRef]);

  const trocarModo = (novoId: ModoRelatorio) => {
    setModoId(novoId);
    // Cada modo tem colunas próprias: manter a seleção anterior deixaria marcadas colunas que
    // não existem no modo novo, e elas seriam descartadas em silêncio pelo servidor.
    const novo = opcoes?.modos.find((item) => item.id === novoId);
    setCamposSelecionados(novo?.padrao ?? []);
  };

  const alternarCampo = (id: string) => {
    setCamposSelecionados((anteriores) =>
      anteriores.includes(id)
        ? anteriores.filter((campo) => campo !== id)
        : [...anteriores, id],
    );
  };

  const marcarTodos = () => {
    if (!modo) return;
    const todos = modo.colunas.map((coluna) => coluna.id);
    setCamposSelecionados(camposSelecionados.length === todos.length ? [] : todos);
  };

  const aoMudarFiltro = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evento.target;
    setFiltros((anteriores) => ({ ...anteriores, [name]: value }));
  };

  const excedeOPdf =
    formato === 'PDF' && !!opcoes && camposSelecionados.length > opcoes.maxColunasPdf;

  const gerar = async () => {
    if (!modo || camposSelecionados.length === 0) return;

    setGerando(true);
    try {
      await gerarRelatorioDeVeiculos({
        campos: camposSelecionados,
        modo: modo.id,
        formato,
        filtros,
        inicio: startDate ? new Date(startDate) : undefined,
        fim: endDate ? new Date(endDate) : undefined,
      });
      notify.success('Relatório gerado. O download foi iniciado.');
      onCancel();
    } catch (erro) {
      /*
       * A resposta é um blob, então a mensagem do servidor não chega pelo caminho comum — e é
       * justamente ela que explica o que fazer ("escolha menos colunas ou gere em CSV").
       */
      const doServidor = await mensagemDeErroDoRelatorio(erro);
      notify.error(
        doServidor ??
          'Não foi possível gerar o relatório. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setGerando(false);
    }
  };

  if (!isOpen) return null;

  const podeGerar =
    !!modo && camposSelecionados.length > 0 && !falhouOCatalogo && !excedeOPdf && !gerando;

  return (
    <div className="cars-report-modal-overlay">
      <div className="cars-report-modal-content" ref={modalRef}>
        <div className="cars-report-modal-header">
          <h2>Gerar Relatório de Veículos</h2>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        {carregandoOpcoes ? (
          <div className="cars-report-modal-loading">
            <p>Carregando as opções do relatório...</p>
          </div>
        ) : (
          <>
            <div className="cars-report-modal-body">
              {falhouOCatalogo && (
                <div className="erro-de-tela">
                  Não foi possível carregar as opções do relatório, então a geração está
                  bloqueada. Sem a lista de colunas, o arquivo sairia incompleto sem aviso.
                  Recarregue a página; se persistir, acione a TI.
                </div>
              )}

              {/* Modo — a granularidade muda o que uma linha significa. */}
              <div className="report-section">
                <div className="section-header">
                  <h3>O que este relatório traz</h3>
                </div>

                <div className="report-modo-grid">
                  {(opcoes?.modos ?? []).map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className={`report-modo-card ${item.id === modoId ? 'active' : ''}`}
                      onClick={() => trocarModo(item.id)}
                    >
                      <span className="report-modo-titulo">{item.titulo}</span>
                      <span className="report-modo-granularidade">{item.granularidade}</span>
                      <span className="report-modo-descricao">{item.descricao}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Formato */}
              <div className="report-section">
                <div className="section-header">
                  <h3>Formato do arquivo</h3>
                  <span className="section-subtitle">
                    O PDF sai com a identidade visual da Atual; o CSV abre no Excel
                  </span>
                </div>

                <div className="report-formato-linha">
                  {(['CSV', 'PDF'] as FormatoRelatorio[]).map((opcao) => (
                    <label
                      key={opcao}
                      className={`checkbox-label ${formato === opcao ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="formato"
                        value={opcao}
                        checked={formato === opcao}
                        onChange={() => setFormato(opcao)}
                      />
                      <span>{opcao === 'CSV' ? 'CSV (planilha)' : 'PDF (para imprimir)'}</span>
                    </label>
                  ))}
                </div>

                {/*
                  Avisa ANTES de gerar, com o número. O servidor recusaria de qualquer forma —
                  o que não pode acontecer é o PDF sair com as colunas cortadas em silêncio.
                */}
                {excedeOPdf && (
                  <p className="report-aviso">
                    O PDF comporta até <strong>{opcoes?.maxColunasPdf}</strong> colunas e você
                    escolheu <strong>{camposSelecionados.length}</strong>. Desmarque colunas ou
                    gere em CSV, que não tem esse limite.
                  </p>
                )}
              </div>

              {/* Colunas, agrupadas por seção */}
              <div className="report-section">
                <div className="section-header">
                  <h3>Colunas no Relatório</h3>
                  <button type="button" className="btn-select-all" onClick={marcarTodos}>
                    {modo && camposSelecionados.length === modo.colunas.length
                      ? 'Desmarcar Todos'
                      : 'Selecionar Todos'}
                  </button>
                </div>

                {secoes.map((secao) => (
                  <div key={secao.nome} className="report-coluna-secao">
                    <h4 className="report-coluna-secao-titulo">{secao.nome}</h4>
                    <div className="checkbox-grid">
                      {secao.colunas.map((coluna) => (
                        <label
                          key={coluna.id}
                          className={`checkbox-label ${
                            camposSelecionados.includes(coluna.id) ? 'active' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            value={coluna.id}
                            checked={camposSelecionados.includes(coluna.id)}
                            onChange={() => alternarCampo(coluna.id)}
                          />
                          <span>{coluna.titulo}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Filtros */}
              <div className="report-section">
                <div className="section-header">
                  <h3>Filtros de Busca</h3>
                </div>

                <div className="filters-grid">
                  <div className="form-field">
                    <label>Placa</label>
                    <input
                      type="text"
                      name="plate"
                      placeholder="Informe a placa"
                      onChange={aoMudarFiltro}
                    />
                  </div>

                  <div className="form-field">
                    <label>Chassi</label>
                    <input
                      type="text"
                      name="chassis"
                      placeholder="Informe o chassi"
                      onChange={aoMudarFiltro}
                    />
                  </div>

                  <div className="form-field">
                    <label>Modelo</label>
                    <input
                      type="text"
                      name="model"
                      placeholder="Informe o modelo"
                      onChange={aoMudarFiltro}
                    />
                  </div>

                  <div className="form-field">
                    <label>Tipo de Dano</label>
                    <select
                      value={filtros.damageType ?? ''}
                      onChange={(evento) =>
                        setFiltros((anteriores) => ({
                          ...anteriores,
                          damageType: evento.target.value,
                        }))
                      }
                    >
                      <option value="">Todos</option>
                      {DamageTypeOptions.map((opcao) => (
                        <option key={opcao} value={opcao}>
                          {DamageTypeMap[opcao]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Dias na Oficina</label>
                    <input
                      type="text"
                      name="daysInShop"
                      placeholder="Informe os dias na oficina"
                      onChange={aoMudarFiltro}
                    />
                  </div>

                  <div className="form-field">
                    <label>Etapa</label>
                    <select
                      value={filtros.esteiraId ?? ''}
                      onChange={(evento) =>
                        // `esteiraId`, não `status`: a chave vai direto para a query string e o
                        // backend só conhece `esteiraId` (at-55).
                        setFiltros((anteriores) => ({
                          ...anteriores,
                          esteiraId: evento.target.value,
                        }))
                      }
                    >
                      <option value="">Todas</option>
                      {esteirasAtivas.map((esteira) => (
                        <option key={esteira.id} value={esteira.id}>
                          {esteira.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Situação de Aprovação</label>
                    <select
                      value={filtros.approvalStatus ?? ''}
                      onChange={(evento) =>
                        setFiltros((anteriores) => ({
                          ...anteriores,
                          approvalStatus: evento.target.value,
                        }))
                      }
                    >
                      <option value="">Todas</option>
                      {Object.entries(ApprovalStatusMap).map(([valor, rotulo]) => (
                        <option key={valor} value={valor}>
                          {rotulo as string}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Colaborador</label>
                    <input
                      type="text"
                      name="collaboratorFullName"
                      placeholder="Informe o nome do colaborador"
                      onChange={aoMudarFiltro}
                    />
                  </div>
                </div>
              </div>

              {/* Período */}
              <div className="report-section">
                <div className="section-header">
                  <h3>Período</h3>
                  {/*
                    at-107: o período filtra a ENTRADA NA OFICINA. Antes era a data de cadastro,
                    e as duas divergem sempre que a recepção cadastra depois — quem filtrava
                    "julho" perdia o veículo que entrou em julho e foi cadastrado em agosto.
                  */}
                  <span className="section-subtitle">
                    Filtra pela data de entrada na oficina. Sem datas, traz todos os veículos
                  </span>
                </div>

                <div className="filters-grid">
                  <div className="form-field">
                    <label>Entrada a partir de</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(evento) => setStartDate(evento.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Entrada até</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(evento) => setEndDate(evento.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="cars-report-modal-footer">
              <button type="button" className="btn-secondary" onClick={onCancel}>
                Fechar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={gerar}
                disabled={!podeGerar}
                title={
                  camposSelecionados.length === 0
                    ? 'Escolha ao menos uma coluna'
                    : undefined
                }
              >
                {gerando ? 'Gerando...' : 'Gerar Relatório'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CarsReportModal;
